import { tr } from '@/lib/i18n-core';

export interface FormFieldSpec {
  key: string;
  labelKey: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'list';
  required?: boolean;
  options?: string[];
}

export const FORM_FIELDS: Record<string, FormFieldSpec[]> = {
  obstetric_history: [
    { key: 'gravida', labelKey: 'cf_gravida', type: 'number', required: true },
    { key: 'para', labelKey: 'cf_para', type: 'number', required: true },
    { key: 'abortions', labelKey: 'cf_abortions', type: 'number', required: true },
    { key: 'living', labelKey: 'cf_living_children', type: 'number', required: true },
    { key: 'lmp', labelKey: 'cf_lmp', type: 'date' },
    { key: 'edd', labelKey: 'cf_edd', type: 'date' },
    { key: 'cycleDays', labelKey: 'cf_cycle_days', type: 'number' },
    { key: 'notes', labelKey: 'notes', type: 'text' },
  ],
  menstrual_history: [
    { key: 'menarcheAgeYears', labelKey: 'cf_menarche_age', type: 'number' },
    { key: 'lmp', labelKey: 'cf_lmp', type: 'date' },
    { key: 'cycleDays', labelKey: 'cf_cycle_days', type: 'number' },
    { key: 'durationDays', labelKey: 'cf_flow_days', type: 'number' },
    { key: 'regular', labelKey: 'cf_regular', type: 'boolean' },
    { key: 'menopause', labelKey: 'cf_menopause', type: 'boolean' },
    { key: 'notes', labelKey: 'notes', type: 'text' },
  ],
  paediatric_history: [
    { key: 'birthWeightKg', labelKey: 'cf_birth_weight_kg', type: 'number' },
    { key: 'gestationWeeks', labelKey: 'cf_gestation_weeks', type: 'number' },
    { key: 'delivery', labelKey: 'cf_delivery', type: 'select', options: ['normal', 'caesarean', 'assisted'] },
    { key: 'feeding', labelKey: 'cf_feeding', type: 'select', options: ['breast', 'formula', 'mixed', 'family_food'] },
    { key: 'milestones', labelKey: 'cf_milestones', type: 'select', options: ['on_time', 'delayed', 'not_assessed'] },
    { key: 'notes', labelKey: 'notes', type: 'text' },
  ],
  immunisation_status: [
    { key: 'doses', labelKey: 'cf_vaccines_given', type: 'list', required: true },
    { key: 'notes', labelKey: 'notes', type: 'text' },
  ],
  growth_measurement: [
    { key: 'weightKg', labelKey: 'vitals_weight_kg', type: 'number' },
    { key: 'heightCm', labelKey: 'vitals_height_cm', type: 'number' },
    { key: 'headCircumferenceCm', labelKey: 'vitals_head_circumference_cm', type: 'number' },
    { key: 'measuredOn', labelKey: 'cf_measured_on', type: 'date' },
  ],
  procedure_note: [
    { key: 'procedure', labelKey: 'cf_procedure', type: 'text', required: true },
    { key: 'indication', labelKey: 'cf_indication', type: 'text' },
    { key: 'anaesthesia', labelKey: 'cf_anaesthesia', type: 'select', options: ['none', 'local', 'sedation', 'regional', 'general'] },
    { key: 'findings', labelKey: 'findings', type: 'text' },
    { key: 'complications', labelKey: 'cf_complications', type: 'text' },
    { key: 'performedBy', labelKey: 'cf_performed_by', type: 'text' },
  ],
  patient_education: [
    { key: 'topics', labelKey: 'cf_topics', type: 'list', required: true },
    { key: 'method', labelKey: 'cf_method', type: 'select', options: ['verbal', 'leaflet', 'demonstration', 'video'], required: true },
    { key: 'understood', labelKey: 'cf_understood', type: 'boolean' },
    { key: 'notes', labelKey: 'notes', type: 'text' },
  ],
  pre_procedure_consent: [
    { key: 'procedure', labelKey: 'cf_procedure', type: 'text', required: true },
    { key: 'risksExplained', labelKey: 'cf_risks_explained', type: 'text', required: true },
    { key: 'signedBy', labelKey: 'cf_signed_by', type: 'text', required: true },
    { key: 'relation', labelKey: 'cf_relation', type: 'select', options: ['self', 'parent', 'spouse', 'guardian', 'other'], required: true },
    { key: 'witness', labelKey: 'cf_witness', type: 'text' },
  ],
};

export function formFieldLabel(spec: Pick<FormFieldSpec, 'labelKey'>): string {
  return tr('opd', spec.labelKey);
}

export function formOptionLabel(option: string): string {
  return tr('opd', `cf_opt_${option}`);
}

export function formMissingRequired(specs: readonly FormFieldSpec[], values: Record<string, string | boolean>): boolean {
  return specs.some((spec) => spec.required && spec.type !== 'boolean' && String(values[spec.key] ?? '').trim() === '');
}

export function formPayload(specs: readonly FormFieldSpec[], values: Record<string, string | boolean>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const spec of specs) {
    const value = values[spec.key];
    if (spec.type === 'boolean') {
      if (value !== undefined) out[spec.key] = value === true;
      continue;
    }
    if (typeof value !== 'string' || value.trim() === '') continue;
    if (spec.type === 'number') out[spec.key] = Number(value);
    else if (spec.type === 'list') {
      const items = value.split(',').map((item) => item.trim()).filter(Boolean);
      out[spec.key] = spec.key === 'doses' ? items.map((vaccine) => ({ vaccine, dose: '', given: true })) : items;
    } else out[spec.key] = value.trim();
  }
  if (specs.some((spec) => spec.key === 'understood') && out.understood === undefined) out.understood = false;
  return out;
}
